import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLoadAllClaudeSpendQuery } from '../app/api';
import { useIsMobile } from './utils';
import UsageTierChart, { TierBadge, ClaudeCodeMark, USAGE_TIERS, matchesTier } from './UsageTierChart';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

// Live Claude-account status for the current month (from the backend, which
// uses the Admin API org-member list when configured). Dollar amounts aside,
// this answers "do they have an account, and are they using it".
const ACCOUNT_META = {
  using:        { label: 'Active',     icon: 'check-circle-fill', bg: 'rgba(26,127,78,0.12)',  fg: brand.green,     br: 'rgba(26,127,78,0.3)' },
  no_account:   { label: 'No Usage',   icon: 'x-circle',          bg: 'rgba(192,57,43,0.10)',  fg: brand.red,       br: 'rgba(192,57,43,0.3)' },
};

function AccountBadge({ status }) {
  const m = ACCOUNT_META[status];
  if (!m) return <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>;
  return (
    <span style={{
      background: m.bg, color: m.fg, border: `1px solid ${m.br}`,
      padding: '2px 9px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center',
    }}>
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
AccountBadge.propTypes = { status: PropTypes.string };

// Claude certification (the onboarding / compliance flow) status. Paired with
// the account badge, this disambiguates a $0 row: e.g. "No Usage" + "Complete"
// = certified but not spending, vs "No Usage" + "Not started" = never onboarded.
const CERT_META = {
  completed:   { label: 'Complete',    icon: 'check-circle-fill', fg: brand.green },
  pending:     { label: 'Pending',     icon: 'hourglass-split',   fg: brand.goldenrod },
  not_started: { label: 'Not started', icon: 'dash-circle',       fg: brand.charcoal },
};

function CertBadge({ status }) {
  const m = CERT_META[status] || CERT_META.not_started;
  return (
    <span style={{ color: m.fg, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
CertBadge.propTypes = { status: PropTypes.string };

// Current month as "YYYY-MM" (UTC, to match the backend's date handling).
function currentMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

const usd = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tooltip text listing each former/alias address and its spend, one per line.
const formerTitle = (others) =>
  (others || []).map(o => `${o.email}: ${usd(o.cost_usd)}`).join('\n') || 'Spend under former addresses';

export default function ExecutiveAI() {
  const isMobile = useIsMobile();
  // Default to a trailing 30-day window so spend stays meaningful right after a
  // month rollover; the month picker remains available for historical months.
  const [rolling, setRolling] = useState(true);
  const [month, setMonth] = useState(currentMonth);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [certFilter, setCertFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [copied, setCopied] = useState(false);

  const { data, isFetching, isError, error } = useLoadAllClaudeSpendQuery(rolling ? {} : { month });
  const configured = data?.configured;
  const employees = useMemo(() => data?.employees || [], [data]);

  const firmOptions = useMemo(
    () => Array.from(new Set(employees.map(e => e.firm).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [employees],
  );

  // Month picker bounded to today so we can't ask for future months.
  const maxMonth = currentMonth();

  // Server already sorts by spend descending; we only filter (never reorder) so
  // the ordering the executive sees stays the authoritative one.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter(e => {
      if (accountFilter !== 'all' && e.account_status !== accountFilter) return false;
      if (certFilter !== 'all' && (e.cert_status || 'not_started') !== certFilter) return false;
      if (firmFilter !== 'all' && (e.firm || '') !== firmFilter) return false;
      if (rolling && !matchesTier(e.activity_30d, tierFilter)) return false;
      if (q && !(e.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [employees, search, accountFilter, certFilter, firmFilter, tierFilter, rolling]);

  // Total reflects the current filter so "Total $X · N of M" all describe the
  // same (filtered) set.
  const totalSpend = useMemo(
    () => filtered.reduce((sum, e) => sum + (e.total_cost_usd ?? e.cost_usd ?? 0), 0),
    [filtered],
  );

  // Activity (messages across all surfaces + weighted Claude Code sessions)
  // only exists in the rolling (last-30-days) view; the server returns
  // activity_30d = null for a specific month.
  const showActivity = rolling;
  const totalActivity = useMemo(
    () => filtered.reduce((sum, e) => sum + (e.activity_30d || 0), 0),
    [filtered],
  );

  // Unique emails of the filtered employees, for the "Copy emails" button.
  const filteredEmails = useMemo(
    () => Array.from(new Set(filtered.map(e => (e.email || '').trim().toLowerCase()).filter(Boolean))),
    [filtered],
  );

  const copyEmails = async () => {
    if (!filteredEmails.length) return;
    try {
      await navigator.clipboard.writeText(filteredEmails.join('; '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      window.alert('Could not copy to clipboard.');
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, width: isMobile ? '100%' : 'auto', flex: isMobile ? 'none' : 1 }}>
            <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
              <i className="bi bi-robot" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
                Executive
              </p>
              <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>AI Spend</h3>
              <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: '0.85rem' }}>
                Claude spend per employee ({rolling ? 'last 30 days' : 'selected month'}), ordered by highest spend.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : 'auto' }}>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: '0.8rem' }}>Spend window</span>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={rolling ? 'rolling' : 'month'}
                onChange={e => setRolling(e.target.value === 'rolling')}
                aria-label="Spend window"
                style={{
                  border: `1px solid ${brand.tealBlue}`, borderRadius: 6,
                  padding: '0.35rem 0.5rem', fontSize: '0.85rem', color: brand.deepBlue,
                  background: 'white', outline: 'none', cursor: 'pointer',
                  flex: isMobile ? '1 1 auto' : '0 0 auto',
                }}
              >
                <option value="rolling">Last 30 days</option>
                <option value="month">Specific month</option>
              </select>
              {!rolling && (
                <input
                  type="month"
                  value={month}
                  max={maxMonth}
                  onChange={e => e.target.value && setMonth(e.target.value)}
                  style={{
                    border: `1px solid ${brand.tealBlue}`, borderRadius: 6,
                    padding: '0.35rem 0.5rem', fontSize: '0.85rem', color: brand.deepBlue,
                    background: 'white', outline: 'none',
                    flex: isMobile ? '1 1 auto' : '0 0 auto',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Not-configured notice */}
      {data && configured === false && (
        <div style={{ background: 'rgba(208,155,44,0.12)', border: `1px solid ${brand.goldenrod}`, borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', color: brand.charcoal, fontSize: '0.85rem' }}>
          <i className="bi bi-info-circle me-1" style={{ color: brand.goldenrod }} />
          Claude spend reporting isn&apos;t configured yet — an admin needs to set the Anthropic Analytics API key. Spend figures will show once it&apos;s in place.
        </div>
      )}
      {isError && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: '1px solid #b00020', color: '#b00020', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {error?.data?.detail || 'Could not load Claude spend for this period.'}
        </div>
      )}

      {/* Toolbar: search + summary */}
      {configured !== false && !isError && (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 1 320px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: brand.charcoal, fontSize: '0.85rem' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name…"
              aria-label="Search employees by name"
              style={{
                width: '100%', padding: '0.5rem 0.75rem 0.5rem 2.1rem',
                border: `1px solid ${brand.softGray}`, borderRadius: 8,
                fontSize: '0.9rem', color: brand.deepBlue, outline: 'none', background: 'white',
              }}
            />
          </div>
          <select
            value={firmFilter}
            onChange={e => setFirmFilter(e.target.value)}
            aria-label="Filter by firm"
            title="Firm"
            style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer', flex: isMobile ? '1 1 45%' : '0 1 auto', minWidth: 0 }}
          >
            <option value="all">All firms</option>
            {firmOptions.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={certFilter}
            onChange={e => setCertFilter(e.target.value)}
            aria-label="Filter by certification status"
            title="Certification status"
            style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer', flex: isMobile ? '1 1 45%' : '0 1 auto', minWidth: 0 }}
          >
            <option value="all">All certification</option>
            <option value="completed">Complete</option>
            <option value="pending">Pending</option>
            <option value="not_started">Not started</option>
          </select>
          <select
            value={accountFilter}
            onChange={e => setAccountFilter(e.target.value)}
            aria-label="Filter by account status"
            title="Account status"
            style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer', flex: isMobile ? '1 1 45%' : '0 1 auto', minWidth: 0 }}
          >
            <option value="all">Any usage</option>
            <option value="using">Active</option>
            <option value="no_account">No Usage</option>
          </select>
          {showActivity && (
            <select
              value={tierFilter}
              onChange={e => setTierFilter(e.target.value)}
              aria-label="Filter by usage category"
              title="Usage category"
              style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.85rem', color: brand.deepBlue, background: 'white', cursor: 'pointer', flex: isMobile ? '1 1 45%' : '0 1 auto', minWidth: 0 }}
            >
              <option value="all">All categories</option>
              {USAGE_TIERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          )}
          <button
            type="button"
            onClick={copyEmails}
            disabled={filteredEmails.length === 0}
            title="Copy the email addresses of the filtered employees"
            style={{ background: copied ? brand.green : brand.goldenrod, color: copied ? 'white' : brand.deepBlue, border: 'none', borderRadius: 8, padding: '0.5rem 0.7rem', fontSize: '0.85rem', fontWeight: 700, cursor: filteredEmails.length ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', opacity: filteredEmails.length ? 1 : 0.6, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
          >
            <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
            {copied ? 'Copied!' : `Copy emails (${filteredEmails.length})`}
          </button>
          <div style={{ marginLeft: isMobile ? 0 : 'auto', width: isMobile ? '100%' : 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', color: brand.charcoal, fontSize: '0.8rem' }}>
            <span style={{ fontWeight: 600 }}>Total:</span>
            <strong style={{ color: brand.deepBlue, fontSize: '1rem' }}>{usd(totalSpend)}</strong>
            {showActivity && (
              <span style={{ opacity: 0.85 }}>· {totalActivity.toLocaleString()} activity</span>
            )}
            <span style={{ opacity: 0.7 }}>· Showing {filtered.length === employees.length ? employees.length : `${filtered.length} of ${employees.length}`}</span>
          </div>
        </div>
      )}

      {/* Usage distribution — engagement tiers by 30-day activity (rolling view only) */}
      {showActivity && configured !== false && !isError && !isFetching && filtered.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <UsageTierChart
            counts={filtered.map(e => e.activity_30d || 0)}
            title="Usage Distribution"
            subtitle="Employees grouped into engagement tiers by activity (messages + Claude Code sessions) in the last 30 days."
          />
        </div>
      )}

      {/* Table */}
      {isFetching ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : configured === false || isError ? null : filtered.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal, background: 'white', borderRadius: 12 }}>
          <i className="bi bi-people" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          {search ? `No employees match “${search}”.` : 'No employees to show.'}
        </div>
      ) : isMobile ? (
        // Mobile: a stacked card per employee. A 6-column table would overflow
        // the viewport and get clipped, so we drop the table entirely on phones.
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {filtered.map((emp, idx) => {
            const spend = emp.total_cost_usd ?? emp.cost_usd;
            return (
              <div key={emp.id} style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(10,41,71,0.12)', padding: '0.85rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: brand.deepBlue, fontWeight: 700, wordBreak: 'break-word' }}>
                      <span style={{ color: brand.charcoal, fontWeight: 600, marginRight: 6 }}>{idx + 1}.</span>{emp.name}
                      {showActivity && <ClaudeCodeMark show={emp.uses_claude_code} />}
                    </div>
                    {emp.firm && <div style={{ color: brand.charcoal, fontSize: '0.78rem' }}>{emp.firm}</div>}
                    {emp.title && <div style={{ color: brand.charcoal, fontSize: '0.78rem' }}>{emp.title}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <strong style={{ color: spend > 0 ? brand.deepBlue : brand.charcoal, fontVariantNumeric: 'tabular-nums' }}>{usd(spend)}</strong>
                    {emp.prior_cost_usd > 0 && (
                      <div style={{ fontSize: '0.68rem', color: brand.charcoal, marginTop: 2, lineHeight: 1.3 }}>
                        {usd(emp.cost_usd)} current ·{' '}
                        <span style={{ color: brand.goldenrod, fontWeight: 600 }} title={formerTitle(emp.other_addresses)}>
                          {usd(emp.prior_cost_usd)} former{emp.other_addresses?.length > 1 ? ` (${emp.other_addresses.length})` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                  {showActivity && <TierBadge count={emp.activity_30d} />}
                  <CertBadge status={emp.cert_status} />
                  <AccountBadge status={emp.account_status} />
                  {showActivity && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: (emp.activity_30d || 0) > 0 ? brand.deepBlue : brand.charcoal }}>
                      <i className="bi bi-activity me-1" style={{ color: brand.charcoal }} />{(emp.activity_30d || 0).toLocaleString()} activity
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: '0 1px 3px rgba(10,41,71,0.12)' }}>
          <table className="table mb-0" style={{ fontSize: '0.9rem', minWidth: 640 }}>
            <thead>
              {/* Header colors set on each cell (background + color) so Bootstrap's
                  .table CSS variables can't override them. */}
              <tr>
                <th style={{ padding: '0.75rem 1rem', border: 'none', width: '3rem', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>#</th>
                <th style={{ padding: '0.75rem 1rem', border: 'none', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>Employee</th>
                <th style={{ padding: '0.75rem 1rem', border: 'none', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>Title</th>
                <th style={{ padding: '0.75rem 1rem', border: 'none', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>Certification</th>
                <th style={{ padding: '0.75rem 1rem', border: 'none', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>Account</th>
                {showActivity && <th style={{ padding: '0.75rem 1rem', border: 'none', textAlign: 'right', background: brand.deepBlue, color: 'white', fontWeight: 700 }} title="Messages across all surfaces + Claude Code sessions (weighted)">Activity<br /><span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'rgba(255,255,255,0.75)' }}>last 30 days</span></th>}
                <th style={{ padding: '0.75rem 1rem', border: 'none', textAlign: 'right', background: brand.deepBlue, color: 'white', fontWeight: 700 }}>Claude spend</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp, idx) => (
                <tr key={emp.id} style={{ borderBottom: `1px solid ${brand.softGray}` }}>
                  <td style={{ padding: '0.65rem 1rem', color: brand.charcoal, verticalAlign: 'middle' }}>
                    {/* Rank within the full (unfiltered) list isn't tracked here; the
                        sequential index is fine since the list stays spend-sorted. */}
                    {idx + 1}
                  </td>
                  <td style={{ padding: '0.65rem 1rem', verticalAlign: 'middle' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ color: brand.deepBlue, fontWeight: 600 }}>{emp.name}{showActivity && <ClaudeCodeMark show={emp.uses_claude_code} />}</span>
                      {showActivity && <TierBadge count={emp.activity_30d} />}
                    </span>
                    {emp.firm && (
                      <span style={{ color: brand.charcoal, fontSize: '0.78rem', display: 'block' }}>{emp.firm}</span>
                    )}
                  </td>
                  <td style={{ padding: '0.65rem 1rem', color: brand.charcoal, verticalAlign: 'middle' }}>
                    {emp.title || '—'}
                  </td>
                  <td style={{ padding: '0.65rem 1rem', verticalAlign: 'middle' }}>
                    <CertBadge status={emp.cert_status} />
                  </td>
                  <td style={{ padding: '0.65rem 1rem', verticalAlign: 'middle' }}>
                    <AccountBadge status={emp.account_status} />
                  </td>
                  {showActivity && (
                    <td style={{ padding: '0.65rem 1rem', textAlign: 'right', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums', color: (emp.activity_30d || 0) > 0 ? brand.deepBlue : brand.charcoal }}>
                      {(emp.activity_30d || 0).toLocaleString()}
                    </td>
                  )}
                  <td style={{ padding: '0.65rem 1rem', textAlign: 'right', verticalAlign: 'middle' }}>
                    <strong style={{ color: (emp.total_cost_usd ?? emp.cost_usd) > 0 ? brand.deepBlue : brand.charcoal, fontVariantNumeric: 'tabular-nums' }}>
                      {usd(emp.total_cost_usd ?? emp.cost_usd)}
                    </strong>
                    {/* Distinguish spend that came in under former/alias email
                        addresses, so a migrated employee's combined total is
                        explainable. Hover the "former" figure to see each
                        address and its spend. */}
                    {emp.prior_cost_usd > 0 && (
                      <div style={{ fontSize: '0.72rem', color: brand.charcoal, marginTop: 2, lineHeight: 1.3 }}>
                        <span title="Spend under current email">{usd(emp.cost_usd)} current</span>
                        {' · '}
                        <span style={{ color: brand.goldenrod, fontWeight: 600, cursor: 'help' }} title={formerTitle(emp.other_addresses)}>
                          <i className="bi bi-clock-history me-1" />{usd(emp.prior_cost_usd)} former
                          {emp.other_addresses?.length > 1 ? ` (${emp.other_addresses.length})` : ''}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showActivity && !isFetching && filtered.some(e => e.uses_claude_code) && (
        <div style={{ marginTop: '0.6rem', color: brand.charcoal, fontSize: '0.75rem' }}>
          <span style={{ color: brand.goldenrod, fontWeight: 800 }}>*</span> also uses Claude Code
        </div>
      )}
    </div>
  );
}
